import type { ChangeEvent, FocusEvent, InputHTMLAttributes, MouseEvent, Ref } from 'react';
import { useCallback, useLayoutEffect, useRef } from 'react';
import { Icon } from '../../icons/Icon';
import { useFieldControl } from '../Field/fieldContext';
import styles from './Checkbox.module.css';

type NativeCheckboxProps = Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'className' | 'disabled' | 'type' | 'size' | 'children'
>;

export interface CheckboxProps extends NativeCheckboxProps {
    /**
     * The visible label beside the box. Required, not optional: a checkbox whose meaning lives only
     * in a neighbouring table header is the commonest unlabelled control in a CRM, and making the
     * prop mandatory is cheaper than catching it in review. Sentence case, per §9.
     */
    readonly label: string;
    /**
     * An id for the visible label text, for a caller that builds the box's name with
     * `aria-labelledby` (the label first, then extra text such as a blocked reason). Reference this
     * id, never the input's own: a form control referenced from its own `aria-labelledby` adds no
     * text in browsers, because its name comes from the wrapping `<label>`, so the label would be lost.
     */
    readonly labelId?: string;
    /** Rendered as `aria-disabled` plus a suppressed toggle — see the component body. */
    readonly disabled?: boolean;
    readonly invalid?: boolean;
    /**
     * The mixed state — a header box over a set of which only some are ticked (design-system §6
     * *Selection*). `indeterminate` is a DOM property with no HTML attribute, so it is set on the input
     * from a layout effect; it carries the mixed state to assistive technology, and `:indeterminate`
     * draws the registry's `indeterminate` glyph in the checked colours.
     */
    readonly indeterminate?: boolean;
    /**
     * Hide the label visually and keep it as the box's name — for a box in a table cell, where the
     * row's own words sit beside it and there is no room for more. The label stays required: a hidden
     * label still names the control. The row then fills its container with `space/4` padding, so the
     * hit area is larger than the box alone (WCAG 2.2 SC 2.5.8).
     */
    readonly labelHidden?: boolean;
    readonly 'data-testid'?: string;
    /**
     * The real `<input>`, for a caller that moves focus to it — the bulk-action bar returns focus to
     * the header box after `Clear`. React 19 passes `ref` as an ordinary prop (`Button`'s note);
     * `| undefined` for `exactOptionalPropertyTypes`, as `Button` types its own.
     */
    readonly ref?: Ref<HTMLInputElement> | undefined;
}

/**
 * A real `<input type="checkbox">` (FS-3 FR-C2) wearing §5's chrome: Input's border family, `accent/gold`
 * when checked, and the registry's tick.
 *
 * **The label is beside the box, not above it** — which is why a checkbox inside a `Field` wants
 * `labels="group"`. In `control` mode the field renders its own `<label for>`, and with this
 * component's inline label that would be two labels for one control; in `group` mode the field's
 * label names the SET ("Certifications") and each box carries its own word. A checkbox that really
 * is the only control in its field can also be used bare, with the field's helper and error slot
 * doing the rest.
 *
 * **Disabled is `aria-disabled` plus a suppressed toggle, never the bare attribute** (§5's matrix,
 * §9's permission rule). `readOnly` does nothing to a checkbox, so the toggle is suppressed in two
 * places instead, and both are load-bearing: `click` is prevented — which also covers Space, since
 * that is the event the keyboard produces — and `change` returns early, because React synthesises
 * `onChange` for checkbox-shaped inputs from the click whether or not the click was prevented. See
 * the comment in `handleChange`; a guard on only the first of those looks correct on screen and
 * still lets a caller's handler run.
 *
 * **Three additions for table selection** (F5-h WI-1.3): `indeterminate` (the header box's mixed
 * state), `labelHidden` (a row box whose name is kept but not shown) and `ref` (the input, for a
 * caller that returns focus to it). Each defaults to the behaviour an existing caller already had.
 *
 * §5 draws the line with Switch: use a checkbox inside a form that has a Save button, and a switch
 * only where the change takes effect immediately.
 */
export function Checkbox({
    label,
    labelId,
    disabled = false,
    invalid,
    indeterminate = false,
    labelHidden = false,
    ref,
    id,
    required,
    'aria-describedby': describedBy,
    'data-testid': testId,
    onBlur,
    onChange,
    onClick,
    ...rest
}: CheckboxProps) {
    const field = useFieldControl({ id, 'aria-describedby': describedBy, invalid, required });
    const inputRef = useRef<HTMLInputElement | null>(null);

    // The caller's ref and this component's own one, on the same input.
    const setInput = useCallback(
        (node: HTMLInputElement | null) => {
            inputRef.current = node;
            if (typeof ref === 'function') {
                ref(node);
            } else if (ref !== null && ref !== undefined) {
                ref.current = node;
            }
        },
        [ref],
    );

    // On every render, not only when the prop changes: a click clears the native property, and a
    // caller whose state did not move would otherwise leave the box out of step with its prop.
    useLayoutEffect(() => {
        if (inputRef.current !== null) {
            inputRef.current.indeterminate = indeterminate;
        }
    });

    function handleBlur(event: FocusEvent<HTMLInputElement>) {
        onBlur?.(event);
        field.reportBlur(event.target.checked ? 'true' : '');
    }

    function handleChange(event: ChangeEvent<HTMLInputElement>) {
        if (disabled) {
            // BOTH guards are needed, and this is the non-obvious one. React synthesises `onChange`
            // for checkbox-shaped inputs from the CLICK event, and it does so whether or not the
            // click was prevented — so `handleClick` alone stops the toggle on screen while the
            // caller's handler still fires. A caller written as `onChange={() => setOn((v) => !v)}`
            // would then flip its own state on a control the user is not allowed to operate.
            return;
        }
        onChange?.(event);
        field.reportChange(event.target.checked ? 'true' : '');
    }

    function handleClick(event: MouseEvent<HTMLInputElement>) {
        if (disabled) {
            event.preventDefault();
            return;
        }
        onClick?.(event);
    }

    return (
        <label className={labelHidden ? `${styles.row} ${styles.filled}` : styles.row}>
            <span className={styles.box}>
                <input
                    {...rest}
                    aria-describedby={field['aria-describedby']}
                    aria-disabled={disabled}
                    aria-invalid={field['aria-invalid']}
                    className={styles.input}
                    data-testid={testId}
                    id={field.id}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    onClick={handleClick}
                    ref={setInput}
                    required={field.required}
                    type="checkbox"
                />
                {/* Decorative — `checked` on the input is what the accessibility tree reads. Shown
                    and hidden from CSS by the input's own :checked state, so the tick cannot get
                    out of step with the control it draws. */}
                <span className={styles.tick}>
                    <Icon emphasis="bold" name="success" size="sm" />
                </span>
                {/* The mixed state's glyph, shown in place of the tick by the input's :indeterminate. */}
                <span className={styles.dash}>
                    <Icon emphasis="bold" name="indeterminate" size="sm" />
                </span>
            </span>
            <span
                className={labelHidden ? `${styles.label} ${styles.visuallyHidden}` : styles.label}
                id={labelId}
            >
                {label}
            </span>
        </label>
    );
}
